import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  siteConfigSchema,
  type SiteConfig,
  type SiteEditorState,
  type SiteImageUpload,
  type SitePreviewLink,
  type SitePublicationSummary,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

/**
 * «Sitio web» (plan feature `website`, docs/plans/ai-first-site.md, phase 3).
 *
 * The draft saves itself; the public site changes only on «Publicar». Keyed on
 * the scoped client, like every client-scoped hook (`client-scope.guard.test.ts`).
 */

const BASE = "/crm/site";

/** How long the editor waits after the last keystroke before saving the draft. */
export const SITE_DRAFT_DEBOUNCE_MS = 800;

export function useSiteEditorState() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["site", clientId],
    queryFn: () => api.get<SiteEditorState>(BASE),
    enabled: !!clientId,
  });
}

export function useSitePublications() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["site-publications", clientId],
    queryFn: () =>
      api.get<{ publications: SitePublicationSummary[] }>(`${BASE}/publications`).then((r) => r.publications),
    enabled: !!clientId,
  });
}

export type SiteSaveStatus = "saved" | "pending" | "saving" | "invalid" | "error";

export type SiteDraftEditor = {
  draft: SiteConfig;
  /** Every validation message, keyed by the field's path (`hero.headline`, `trust.items.0.name`). */
  errors: ReadonlyMap<string, string>;
  status: SiteSaveStatus;
  /** The local draft holds something the server does not have yet. */
  dirty: boolean;
  update: (recipe: (draft: SiteConfig) => SiteConfig) => void;
  /** Saves now whatever is pending. False when something could not be saved (invalid or failed). */
  flush: () => Promise<boolean>;
  /** Replaces the local draft with one the server just produced (a restore). */
  reseed: (draft: SiteConfig) => void;
};

function issuesOf(draft: SiteConfig): Map<string, string> {
  const parsed = siteConfigSchema.safeParse(draft);
  const errors = new Map<string, string>();
  if (parsed.success) return errors;
  for (const issue of parsed.error.issues) {
    const path = issue.path.join(".");
    if (!errors.has(path)) errors.set(path, issue.message);
  }
  return errors;
}

/**
 * The local draft and its autosave.
 *
 * The local copy is the source of truth while the page is open: a save's
 * response refreshes the publish state, never the fields, so a reply landing
 * mid-keystroke cannot rewind what the admin is typing. A draft that does not
 * pass `siteConfigSchema` is NOT sent — the server would 400 it — and the status
 * says so until it is fixed.
 */
export function useSiteDraft(initial: SiteConfig | undefined): SiteDraftEditor {
  const clientId = useScopedClientId();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<SiteConfig | undefined>(initial);
  const [status, setStatus] = useState<SiteSaveStatus>("saved");
  const lastSaved = useRef<string | null>(initial ? JSON.stringify(initial) : null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const latest = useRef<SiteConfig | undefined>(initial);

  // First load: adopt the server's draft. A client switch remounts the editor
  // (SitioWeb keys it by client), so this never carries one tenant's draft to another.
  useEffect(() => {
    if (!initial || latest.current) return;
    latest.current = initial;
    lastSaved.current = JSON.stringify(initial);
    setDraft(initial);
  }, [initial]);

  const errors = useMemo(() => (draft ? issuesOf(draft) : new Map<string, string>()), [draft]);

  const save = useCallback(async (): Promise<boolean> => {
    if (inFlight.current) await inFlight.current;
    const current = latest.current;
    if (!current) return true;
    const json = JSON.stringify(current);
    if (json === lastSaved.current) {
      setStatus("saved");
      return true;
    }
    const parsed = siteConfigSchema.safeParse(current);
    if (!parsed.success) {
      setStatus("invalid");
      return false;
    }
    setStatus("saving");
    const request = (async () => {
      try {
        const state = await api.put<SiteEditorState>(`${BASE}/draft`, parsed.data);
        lastSaved.current = json;
        queryClient.setQueryData<SiteEditorState>(["site", clientId], (old) =>
          old ? { ...state, draft: old.draft } : state,
        );
        const now = latest.current;
        if (JSON.stringify(now) === json) setStatus("saved");
        else setStatus(now && siteConfigSchema.safeParse(now).success ? "pending" : "invalid");
        return true;
      } catch {
        setStatus("error");
        return false;
      }
    })();
    inFlight.current = request;
    const ok = await request;
    inFlight.current = null;
    return ok;
  }, [clientId, queryClient]);

  const schedule = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void save().then((ok) => {
        // An edit made while this save was in flight still needs its own.
        if (ok && JSON.stringify(latest.current) !== lastSaved.current) schedule();
      });
    }, SITE_DRAFT_DEBOUNCE_MS);
  }, [save]);

  const update = useCallback(
    (recipe: (draft: SiteConfig) => SiteConfig) => {
      const base = latest.current;
      if (!base) return;
      const next = recipe(base);
      latest.current = next;
      setDraft(next);
      if (!siteConfigSchema.safeParse(next).success) {
        if (timer.current) clearTimeout(timer.current);
        setStatus("invalid");
        return;
      }
      setStatus("pending");
      schedule();
    },
    [schedule],
  );

  const flush = useCallback(async (): Promise<boolean> => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    return save();
  }, [save]);

  const reseed = useCallback((next: SiteConfig) => {
    if (timer.current) clearTimeout(timer.current);
    latest.current = next;
    lastSaved.current = JSON.stringify(next);
    setDraft(next);
    setStatus("saved");
  }, []);

  // Leaving the page with a debounce pending must not drop the edit.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void save();
      }
    },
    [save],
  );

  const dirty = draft !== undefined && JSON.stringify(draft) !== lastSaved.current;
  return {
    draft: draft ?? siteConfigSchema.parse({}),
    errors,
    status,
    dirty,
    update,
    flush,
    reseed,
  };
}

function useInvalidateSite() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["site"] });
    void queryClient.invalidateQueries({ queryKey: ["site-publications"] });
  };
}

export function usePublishSite() {
  const invalidate = useInvalidateSite();
  return useMutation({
    mutationFn: () => api.post<SiteEditorState>(`${BASE}/publish`, {}),
    onSuccess: () => {
      invalidate();
      toast.success("Sitio publicado. Se verá en el dominio en cerca de un minuto.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/** Rollback: the draft becomes that version and it is published again. */
export function useRestoreSitePublication() {
  const invalidate = useInvalidateSite();
  return useMutation({
    mutationFn: (publicationId: string) =>
      api.post<SiteEditorState>(`${BASE}/publications/${publicationId}/restore`, {}),
    onSuccess: () => {
      invalidate();
      toast.success("Versión restaurada y publicada.");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useCreateSitePreview() {
  return useMutation({
    mutationFn: () => api.post<SitePreviewLink>(`${BASE}/preview`, {}),
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUploadSiteImage() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api.postForm<SiteImageUpload>(`${BASE}/images`, form);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
